import { UserRightsFunctionModel } from "../../models/rights/user-rights-function/user-rights-function.model";
import { UserRightsFunctionDetailMenuEnum } from "../../enums/rights/UserRightsFunctionDetailMenuEnum";
import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { UserRightsFunctionDetailAction } from "./user-rights-function-detail.action";
import { finalize, Observable, of, throwError } from "rxjs";
import { map } from "rxjs/operators";
import { delayValueRx, RequestClientService } from '../../services/client/request-client.service';
import { UserPrivilegeRequestService } from "../../services/user-privilege-request.service";
import {
  UserRightsFunctionToUserPrivilegesDTO
} from "../../models/rights/user-rights-function/UserRightsFunctionToUserPrivilegesDTO";
import {
  UserRightsFunctionToUserPrivilegeSetDtoModel
} from "../../models/rights/user-rights-function/user-rights-function-to-user-privilege-set-dto.model";
import { UserPrivilegeSetApiService } from "../../services/user-privilege-set-api.service";
import { ResetStateDefaults } from "../base-action-class.model";
import { UserRightsFunctionRequestService } from '../../services/request/user-rights-function-request.service';
import { RUserRoleXsState } from '../user-rights/topic-based/user-role/r-user-role-xs.state';
import { RUserFunctionXsState } from '../user-rights/topic-based/user-function/r-user-function-xs.state';

interface UserRightsFunctionDetailXsStateModel {
  id: string;
  userRightsFunction: UserRightsFunctionModel;
  selectedMenu: UserRightsFunctionDetailMenuEnum;
  inProgress: boolean;
  deletable: boolean;
  editable: boolean;

  assignPrivilegeInProgress: boolean;
  assignPrivilegeInProgressFor: string;

  assignPrivilegeSetInProgress: boolean;
  assignPrivilegeSetInProgressFor: string;
}

const getDefaults = (): UserRightsFunctionDetailXsStateModel => ({
  id: null,
  userRightsFunction: null,
  selectedMenu: UserRightsFunctionDetailMenuEnum.BASIC_INFOS,
  inProgress: false,
  deletable: false,
  editable: false,
  assignPrivilegeInProgress: false,
  assignPrivilegeInProgressFor: null,
  assignPrivilegeSetInProgress: false,
  assignPrivilegeSetInProgressFor: null
});

@State<UserRightsFunctionDetailXsStateModel>({
  name: 'userRightsFunctionDetail',
  defaults: getDefaults()
})
@Injectable()
export class UserRightsFunctionDetailXsState {

  constructor(
    private userRightsFunctionRequestService: UserRightsFunctionRequestService,
    private userPrivilegeService: UserPrivilegeRequestService,
    private store: Store,
    private userPrivilegeSetApiService: UserPrivilegeSetApiService
  ) {
  }

  //region selectors
  @Selector()
  static userRightsFunction(state: UserRightsFunctionDetailXsStateModel): UserRightsFunctionModel | null {
    return state.userRightsFunction || null;
  }

  @Selector()
  static deletable(state: UserRightsFunctionDetailXsStateModel): boolean {
    return state.deletable;
  }

  @Selector()
  static editable(state: UserRightsFunctionDetailXsStateModel): boolean {
    return state.editable;
  }

  @Selector()
  static userRightsFunctionId(state: UserRightsFunctionDetailXsStateModel): string | null {
    return state.id || null;
  }

  @Selector()
  static assignedPrivilegeIds(state: UserRightsFunctionDetailXsStateModel): string[] {
    return state.userRightsFunction.assignedPrivilegeIds || [];
  }

  @Selector()
  static assignedPrivilegeSetIds(state: UserRightsFunctionDetailXsStateModel): string[] {
    return state.userRightsFunction.assignedPrivilegeSetIds || [];
  }

  @Selector()
  static assignedPrivilegeInProgress(state: UserRightsFunctionDetailXsStateModel): boolean {
    return state.assignPrivilegeInProgress;
  }

  @Selector()
  static assignedPrivilegeInProgressFor(state: UserRightsFunctionDetailXsStateModel): string | null {
    return state.assignPrivilegeInProgressFor;
  }

  @Selector()
  static assignedPrivilegeSetInProgress(state: UserRightsFunctionDetailXsStateModel): boolean {
    return state.assignPrivilegeSetInProgress;
  }

  @Selector()
  static assignedPrivilegeSetInProgressFor(state: UserRightsFunctionDetailXsStateModel): string | null {
    return state.assignPrivilegeSetInProgressFor;
  }

  @Selector()
  static selectedMenu(state: UserRightsFunctionDetailXsStateModel): UserRightsFunctionDetailMenuEnum | null {
    return state.selectedMenu || null;
  }

  @Selector()
  static inProgress(state: UserRightsFunctionDetailXsStateModel): boolean {
    return state.inProgress;
  }

  //endregion selectors

  //region actions
  @Action(UserRightsFunctionDetailAction.LoadUserRightsFunction)
  public loadUserRightsFunction(ctx: StateContext<UserRightsFunctionDetailXsStateModel>, action: UserRightsFunctionDetailAction.LoadUserRightsFunction): Observable<void> {

    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User rights function request is in progress!'));
    }
    if (!action.id) {
      return throwError(() => new Error('User rights function id must be set!'));
    }
    if (state.id === action.id) {
      return of(null);
    }

    ctx.patchState({
      id: null,
      userRightsFunction: null
    });

    const start = new Date();

    return this.userRightsFunctionRequestService.getById(action.id)
      .pipe(
        delayValueRx(start),
        map((userRightsFunctionModel: UserRightsFunctionModel) => {
          ctx.patchState({
            id: userRightsFunctionModel.id,
            userRightsFunction: userRightsFunctionModel,
            deletable: this.store.selectSnapshot(RUserFunctionXsState.canDeleteUserFunctionByModel(userRightsFunctionModel)),
            editable: this.store.selectSnapshot(RUserFunctionXsState.canEditUserFunctionByModel(userRightsFunctionModel)),
          });
        })
      );
  }

  @Action(UserRightsFunctionDetailAction.UpdateBasicInfo)
  public updateBasicInfo(ctx: StateContext<UserRightsFunctionDetailXsStateModel>, action: UserRightsFunctionDetailAction.UpdateBasicInfo): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User rights function request is in progress!'));
    }
    if (!action.basicInfo) {
      return throwError(() => new Error('Basic info must be set!'));
    }
    if (!action.basicInfo.id) {
      return throwError(() => new Error('Id must be set!'));
    }
    if (!action.basicInfo.nameCV) {
      return throwError(() => new Error('NameCV must be set!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const start = new Date();

    return this.userRightsFunctionRequestService.updateRx(action.basicInfo)
      .pipe(
        delayValueRx(start),
        map((userRightsFunction: UserRightsFunctionModel) => {
          ctx.patchState({
            userRightsFunction: userRightsFunction,
            deletable: this.store.selectSnapshot(RUserFunctionXsState.canDeleteUserFunctionByModel(userRightsFunction)),
            editable: this.store.selectSnapshot(RUserFunctionXsState.canEditUserFunctionByModel(userRightsFunction)),
          });
        }),
        finalize(() => {
          ctx.patchState({inProgress: false});
        })
      );
  }

  @Action(UserRightsFunctionDetailAction.SavePrivilege)
  public savePrivilege(ctx: StateContext<UserRightsFunctionDetailXsStateModel>, action: UserRightsFunctionDetailAction.SavePrivilege): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User rights function request is in progress!'));
    }
    if (!state.id) {
      return throwError(() => new Error('No user rights function is loaded!'));
    }
    if (!action.privilegeId) {
      return throwError(() => new Error('No privilege id!'));
    }

    ctx.patchState({
      inProgress: true,
      assignPrivilegeInProgress: true,
      assignPrivilegeInProgressFor: action.privilegeId
    });

    const requestBody = new UserRightsFunctionToUserPrivilegesDTO();
    requestBody.userRightsFunctionId = state.id;
    requestBody.userPrivilegeIds = [action.privilegeId];

    const start = new Date();

    return this.userPrivilegeService.addUserPrivilegesToUserRightsFunctionRx(requestBody)
      .pipe(
        delayValueRx(start),
        map((added: boolean) => {
          if (added) {
            const userRightsFunction = {...state.userRightsFunction};
            userRightsFunction.assignedPrivilegeIds = [...state.userRightsFunction.assignedPrivilegeIds, action.privilegeId];

            ctx.patchState({
              userRightsFunction: userRightsFunction,
              deletable: this.store.selectSnapshot(RUserFunctionXsState.canDeleteUserFunctionByModel(userRightsFunction)),
              editable: this.store.selectSnapshot(RUserFunctionXsState.canEditUserFunctionByModel(userRightsFunction)),
            });
          }
        }),
        finalize(() => {
          ctx.patchState({
            inProgress: false,
            assignPrivilegeInProgress: false,
            assignPrivilegeInProgressFor: null
          });
        })
      );
  }

  @Action(UserRightsFunctionDetailAction.DeletePrivilege)
  public deletePrivilege(ctx: StateContext<UserRightsFunctionDetailXsStateModel>, action: UserRightsFunctionDetailAction.DeletePrivilege): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User rights function request is in progress!'));
    }
    if (!state.id) {
      return throwError(() => new Error('No user rights function is loaded!'));
    }
    if (!action.privilegeId) {
      return throwError(() => new Error('No privilege id!'));
    }

    ctx.patchState({
      inProgress: true,
      assignPrivilegeInProgress: true,
      assignPrivilegeInProgressFor: action.privilegeId
    });

    const requestBody = new UserRightsFunctionToUserPrivilegesDTO();
    requestBody.userRightsFunctionId = state.id;
    requestBody.userPrivilegeIds = [action.privilegeId];

    const start = new Date();

    return this.userPrivilegeService.removeUserPrivilegesFromUserRightsFunctionRx(requestBody)
      .pipe(
        delayValueRx(start),
        map((added: boolean) => {
          if (added) {
            const userRightsFunction = {...state.userRightsFunction};
            userRightsFunction.assignedPrivilegeIds = [...state.userRightsFunction.assignedPrivilegeIds.filter(e => e !== action.privilegeId)];

            ctx.patchState({
              userRightsFunction: userRightsFunction,
              deletable: this.store.selectSnapshot(RUserFunctionXsState.canDeleteUserFunctionByModel(userRightsFunction)),
              editable: this.store.selectSnapshot(RUserFunctionXsState.canEditUserFunctionByModel(userRightsFunction)),
            });
          }
        }),
        finalize(() => {
          ctx.patchState({
            inProgress: false,
            assignPrivilegeInProgress: false,
            assignPrivilegeInProgressFor: null
          });
        })
      );
  }

  @Action(UserRightsFunctionDetailAction.SavePrivilegeSet)
  public savePrivilegeSet(ctx: StateContext<UserRightsFunctionDetailXsStateModel>, action: UserRightsFunctionDetailAction.SavePrivilegeSet): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User rights function request is in progress!'));
    }
    if (!state.id) {
      return throwError(() => new Error('No user rights function is loaded!'));
    }
    if (!action.privilegeSetId) {
      return throwError(() => new Error('No privilege id!'));
    }

    ctx.patchState({
      inProgress: true,
      assignPrivilegeSetInProgress: true,
      assignPrivilegeSetInProgressFor: action.privilegeSetId
    });

    const requestBody = new UserRightsFunctionToUserPrivilegeSetDtoModel();
    requestBody.userRightsFunctionId = state.id;
    requestBody.userPrivilegeSetIds = [action.privilegeSetId];

    const start = new Date();

    return this.userPrivilegeSetApiService.addUserPrivilegeSetsToUserRightsFunctionRx(requestBody)
      .pipe(
        delayValueRx(start),
        map((added: boolean) => {
          if (added) {
            const userRightsFunction = {...state.userRightsFunction};
            userRightsFunction.assignedPrivilegeSetIds = [...state.userRightsFunction.assignedPrivilegeSetIds, action.privilegeSetId];

            ctx.patchState({
              userRightsFunction: userRightsFunction,
              deletable: this.store.selectSnapshot(RUserFunctionXsState.canDeleteUserFunctionByModel(userRightsFunction)),
              editable: this.store.selectSnapshot(RUserFunctionXsState.canEditUserFunctionByModel(userRightsFunction)),
            });
          }
        }),
        finalize(() => {
          ctx.patchState({
            inProgress: false,
            assignPrivilegeSetInProgress: false,
            assignPrivilegeSetInProgressFor: null
          });
        })
      );
  }

  @Action(UserRightsFunctionDetailAction.DeletePrivilegeSet)
  public deletePrivilegeSet(ctx: StateContext<UserRightsFunctionDetailXsStateModel>, action: UserRightsFunctionDetailAction.DeletePrivilegeSet): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User rights function request is in progress!'));
    }
    if (!state.id) {
      return throwError(() => new Error('No user rights function is loaded!'));
    }
    if (!action.privilegeSetId) {
      return throwError(() => new Error('No privilege id!'));
    }

    ctx.patchState({
      inProgress: true,
      assignPrivilegeSetInProgress: true,
      assignPrivilegeSetInProgressFor: action.privilegeSetId
    });

    const requestBody = new UserRightsFunctionToUserPrivilegeSetDtoModel();
    requestBody.userRightsFunctionId = state.id;
    requestBody.userPrivilegeSetIds = [action.privilegeSetId];

    const start = new Date();

    return this.userPrivilegeSetApiService.removeUserPrivilegeSetsFromUserRightsFunctionRx(requestBody)
      .pipe(
        delayValueRx(start),
        map((added: boolean) => {
          if (added) {
            const userRightsFunction = {...state.userRightsFunction};
            userRightsFunction.assignedPrivilegeSetIds = [...state.userRightsFunction.assignedPrivilegeSetIds.filter(e => e !== action.privilegeSetId)];

            ctx.patchState({
              userRightsFunction: userRightsFunction,
              deletable: this.store.selectSnapshot(RUserFunctionXsState.canDeleteUserFunctionByModel(userRightsFunction)),
              editable: this.store.selectSnapshot(RUserFunctionXsState.canEditUserFunctionByModel(userRightsFunction)),
            });
          }
        }),
        finalize(() => {
          ctx.patchState({
            inProgress: false,
            assignPrivilegeSetInProgress: false,
            assignPrivilegeSetInProgressFor: null
          });
        })
      );
  }


  @Action(UserRightsFunctionDetailAction.Delete)
  public delete(ctx: StateContext<UserRightsFunctionDetailXsStateModel>, action: UserRightsFunctionDetailAction.Delete): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User rights function request is in progress!'));
    }
    if (!state.id) {
      return throwError(() => new Error('No user rights function is loaded!'));
    }

    ctx.patchState({
      inProgress: true
    });

    return this.userRightsFunctionRequestService.deleteRx(state.id)
      .pipe(
        map(() => {
          ctx.patchState({
            userRightsFunction: null,
            deletable: false,
            editable: false,
          });
        }),
        finalize(() => {
          ctx.patchState({inProgress: false});
        })
      );
  }

  @Action([UserRightsFunctionDetailAction.ResetState, ResetStateDefaults])
  public resetState(ctx: StateContext<UserRightsFunctionDetailXsStateModel>) {
    ctx.patchState({...getDefaults()});
  }

  @Action(UserRightsFunctionDetailAction.SelectMenu)
  public selectMenu(ctx: StateContext<UserRightsFunctionDetailXsStateModel>, action: UserRightsFunctionDetailAction.SelectMenu): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User rights function request is in progress!'));
    }
    if (!action.menu) {
      return throwError(() => new Error('Menu must be set!'));
    }

    ctx.patchState({
      selectedMenu: action.menu
    });
  }

  //endregion actions
}
